import type { NovelItem } from '@app/_proto/Protos/novels';
import type { SponsorPlanItem } from '@app/_proto/Protos/sponsors';
import type { SubscriptionItem } from '@app/_proto/Protos/subscriptions';
import Countdown from '@app/components/shared/Countdown';
import ChampionPlans from '@app/domains/champion/components/ChampionPlans';
import { championDiscountState } from '@app/domains/champion/utils/championDiscountState';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

type ChampionDiscountCallToActionProps = {
    novel: NovelItem;
    currentSubscription: SubscriptionItem | null;
    sortedSponsorPlans: SponsorPlanItem[];
    onSelectPlan?: (plan: SponsorPlanItem) => void;
    refetchData?: () => void;
};

export default function ChampionDiscountCallToAction({
    novel,
    currentSubscription,
    sortedSponsorPlans,
    onSelectPlan,
    refetchData,
}: ChampionDiscountCallToActionProps) {
    const [isShowingAllTiers, setIsShowingAllTiers] = useState(false);

    const { plansWithDiscount, discountText, endsAt } = useMemo(() => {
        return championDiscountState(currentSubscription, sortedSponsorPlans);
    }, [sortedSponsorPlans, currentSubscription]);

    const toggleSeeMoreTiers = () => {
        setIsShowingAllTiers(prev => !prev);
    };

    if (!plansWithDiscount || plansWithDiscount.length === 0) {
        return null;
    }

    return (
        <div className="mt-42">
            <div
                className={clsx(
                    'bg-gray-100 dark:bg-black rounded-10 p-24 flex flex-col gap-y-[32px] sm:gap-x-[32px] sm:gap-y-0 sm:flex-row',
                    {
                        '!flex-col !gap-y-[32px]': plansWithDiscount.length > 1,
                    }
                )}
            >
                <div className="flex-1">
                    <img
                        className="h-32 w-140"
                        width={140}
                        height={32}
                        src="/images/champion-logo@3x.png"
                        alt="champion logo"
                    />
                    <div className="pt-8 font-bold text-black dark:text-white text-[28px] leading-[32.2px]">
                        Get <span className="text-blue-500">{discountText}</span> for a limited time only.
                    </div>
                    <div className="text-gray-750 dark:text-gray-400 text-[15.2px] leading-[18.24px] font-medium pt-12">
                        Become a Champion today. Support authors and translators, and be rewarded for it!
                    </div>
                </div>
                <div
                    className={clsx('flex-1', {
                        'bg-blue-600 rounded-t-8 rounded-b-12': !!discountText,
                    })}
                >
                    {discountText && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-10 px-8 pt-8 pb-8 sm:pb-20">
                            <div className="uppercase bg-yellow-600 px-10 py-5 rounded-4 text-[14px] leading-[16.8px] font-bold text-black">
                                {discountText} now!
                            </div>
                            {endsAt && <Countdown endsAt={endsAt} countdownEnded={refetchData} />}
                        </div>
                    )}
                    <div
                        className={clsx('', {
                            'pb-10 px-10': plansWithDiscount.length > 1,
                        })}
                    >
                        <ChampionPlans
                            novelId={novel.id}
                            sponsorPlans={isShowingAllTiers ? plansWithDiscount : plansWithDiscount?.slice(0, 2)}
                            onSelectPlan={onSelectPlan}
                            currentSubscription={null}
                            gapX={16}
                            gapY={12}
                            maxColumn={plansWithDiscount.length > 1 ? 2 : 1}
                            descLines={1}
                        />
                    </div>
                </div>
                {plansWithDiscount.length > 2 && (
                    <button type="button" className="font-set-sb15 text-gray-desc" onClick={toggleSeeMoreTiers}>
                        {isShowingAllTiers ? 'Show less tiers' : 'Show more tiers'}
                    </button>
                )}
            </div>
        </div>
    );
}
